import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ChannelsService } from 'app/infrastructure/services/channels.service';
import { AuthorizationConfigurationService } from 'app/infrastructure/services/authorizationConfiguration.service';
import { AlertService, GrowlType } from 'app/infrastructure/services/alert.service';
import { Translator } from 'angular-translator';
import { AuthorizationConfiguration } from 'app/infrastructure/model/classes/authorizationConfiguration.class';
import { AuthorizationConfigurationIp } from "app/infrastructure/model/classes/authorizationConfigurationIp.class";
import { KeyValueBuilderComponent } from 'app/helpers/components/kevValueBuilder/keyValueBuilder.component';
import { Channel } from 'app/infrastructure/model/interfaces/channel.interface';

@Component({
    selector: 'sx-authorization-configuration-channel',
    templateUrl: './authorizationConfiguration.channel.component.html',
    styleUrls: ['./authorizationConfiguration.channel.component.css'],
})
export class AuthorizationConfigurationChannelComponent implements OnInit {
    @ViewChild('ipList') ipList: KeyValueBuilderComponent;

    subscriptionCode: string;
    channelCode: string;
    organizationCode: string;
    channel: Channel;
    configuration: AuthorizationConfiguration;
    configurations: AuthorizationConfiguration[];
    stringifiedIps: string;

    availableUsers: string[];

    private enableConstant = 'A';
    private disableConstant = 'B';

    constructor(
        private aRoute: ActivatedRoute,
        private router: Router,
        private channelService: ChannelsService,
        private authorizationConfigurationService: AuthorizationConfigurationService,
        private alertService: AlertService,
        private translate: Translator
    ) { }

    ngOnInit() {
        this.aRoute.params.subscribe((params: Params) => {
            this.organizationCode = params['codorganizacion']
            this.subscriptionCode = params['codsistema'];
            this.channelCode = params['codnegocio'];

            this.channelService.getDetailChannel(this.organizationCode, this.subscriptionCode, this.channelCode).subscribe(data => {
                if (!data) {
                    return this.router.navigateByUrl('/soc/404');
                }

                this.channel = data;
                this.authorizationConfigurationService.getAll(this.subscriptionCode, this.channelCode).subscribe( configs => {
                    this.configurations = configs;
                });

                this.channelService.getUsers(this.subscriptionCode,this.channelCode)
                .subscribe( usrs => { this.availableUsers = usrs; });
                this.initializeConfiguration();
            });
        });
    }

    save() {
        this.configuration.ips = JSON.parse(this.ipList.items);

        if (this.isValidConfiguration()) {
            this.authorizationConfigurationService.save(this.configuration).then(config => {
                this.alertService.showGrowl({
                    type: GrowlType.SUCCESS,
                    body: 'AUTHORIZATION_CONFIGURATION_SUCCESS',
                });

                this.configurations.push(config);
                this.ipList._items = [];
                this.initializeConfiguration();
            });
        }
    }

    generateApiKey() {
        if (this.isValidForApiKeyGeneration()) {
            this.authorizationConfigurationService.generateApiKey(this.configuration).then(key => this.configuration.apiKey = key);
        }
    }

    disableConfiguration(config: AuthorizationConfiguration) {
        this.authorizationConfigurationService.disable(this.subscriptionCode, this.channelCode, config.id).then(result => {
            if (result) {
                config.status = this.disableConstant;
                this.alertService.showGrowl({
                    type: GrowlType.SUCCESS,
                    body: 'AUTHORIZATION_CONFIGURATION_DISABLE_SUCCESS',
                });
            }
        });
    }

    enableConfiguration(config: AuthorizationConfiguration) {
        this.authorizationConfigurationService.enable(this.subscriptionCode, this.channelCode, config.id).then(result => {
            if (result) {
                config.status = this.enableConstant;
                this.alertService.showGrowl({
                    type: GrowlType.SUCCESS,
                    body: 'AUTHORIZATION_CONFIGURATION_ENABLE_SUCCESS',
                });
            }
        });
    }

    copyApiKeyToClipboard(config: AuthorizationConfiguration) {
        const selBox = document.createElement('textarea');
        selBox.style.position = 'fixed';
        selBox.style.left = '0';
        selBox.style.top = '0';
        selBox.style.opacity = '0';
        selBox.value = config.apiKey;
        document.body.appendChild(selBox);
        selBox.focus();
        selBox.select();
        document.execCommand('copy');
        document.body.removeChild(selBox);
    }


    enableAccess() {
        this.channelService.enableApiAccess(this.subscriptionCode, this.channelCode, this.channel).then(result => {
            if (result) {
                if(!this.channel.enableApiAccess){
                    this.alertService.showGrowl({
                        type: GrowlType.SUCCESS,
                        body: 'DISABLE_API_ACCESS_SUCCESS',
                    });
                }else{
                    this.alertService.showGrowl({
                        type: GrowlType.SUCCESS,
                        body: 'ENABLE_API_ACCESS_SUCCESS',
                    });
                }
            }
        });
    }

    isValidForApiKeyGeneration() {
        return this.configuration.apiKeyValidation && this.configuration.username && this.configuration.keyExpiryDate;
    }

    isValidConfiguration() {
        let isValid = true;
        if (!this.configuration.ipValidation && !this.configuration.apiKeyValidation) {
            this.alertService.showGrowl({
                type: GrowlType.WARNING,
                body: 'PLEASE_SELECT_VALIDATION_API_KEY_IP',
            });
            isValid = false;
        }

        if (this.configuration.apiKeyValidation && this.configuration.keyExpiryDate  <  new Date() ) {
            this.alertService.showGrowl({
                type: GrowlType.WARNING,
                body: 'INVALID_EXPIRATION_DATE',
            });

            isValid = false;
        }

        if (this.configuration.apiKeyValidation && this.configuration.tokenDuration  <  0 ) {
            this.alertService.showGrowl({
                type: GrowlType.WARNING,
                body: 'INVALID_TOKEN_DURATION',
            });
            isValid = false;
        }

        if (this.configuration.ipValidation && this.configuration.ips.length === 0) {
            this.alertService.showGrowl({
                type: GrowlType.WARNING,
                body: 'PLEASE_ADD_IPS',
            });

            isValid = false;
        }

        if (this.configuration.apiKeyValidation && (
            !this.configuration.apiKey ||
            !this.configuration.keyExpiryDate ||
            !this.configuration.username ||
            !this.configuration.tokenDuration)) {
            this.alertService.showGrowl({
                type: GrowlType.WARNING,
                body: 'PLEASE_FILL_API_KEY_FIELDS',
            });

            isValid = false;
        }

        return isValid;
    }
    clearApiTokens()
    {
        this.authorizationConfigurationService.clearApiTokens(this.subscriptionCode, this.channelCode).then(result => {
            if (result) {
                // config.status = this.disableConstant;
                this.alertService.showGrowl({
                    type: GrowlType.SUCCESS,
                    body: 'DISABLE_API_TOKENS_SUCCESS',
                });
            }
        });
    }
    back() {
        this.router.navigate(['../'], { relativeTo: this.aRoute });
    }

    changeDate(value) {
        this.configuration.keyExpiryDate = value;
    }

    /**
     * Validate Item List is Empty
     */
    itemListIsEmpty(itemList: KeyValueBuilderComponent): boolean {
        return itemList === undefined || itemList.items === '[[]]' || itemList.items === '[]';
    }

    isDisableConfiguration(config: AuthorizationConfiguration) {
        return config.status === this.disableConstant;
    }

    isEnableConfiguration(config: AuthorizationConfiguration) {
        return config.status === this.enableConstant;
    }

    hasConfigurations() {
        return this.configurations !== undefined && this.configurations !== null && this.configurations.length > 0;
    }

    private initializeConfiguration() {
        this.configuration = {
            systemCode: this.subscriptionCode,
            businessCode: this.channelCode,
            apiKey: null,
            apiKeyValidation: false,
            creado: null,
            fechaActualizacion: null,
            fechaCreacion: null,
            id: null,
            ipValidation: false,
            ips: [],
            keyExpiryDate: null,
            modificado: null,
            status: this.enableConstant,
            tokenDuration: null,
            username: null,
        };

        this.stringifiedIps = JSON.stringify(this.toKVBelem([]));
    }

    private toKVBelem(range: string[]) {
        return range.map(s => { return { value: s }; });
    }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/channels/authorizationConfiguration/authorizationConfiguration.channel.component.ts